// withFetchProductData.js
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { LRUCache } from 'lru-cache';

import { MESSAGE_PACK_FEED_ALIAS } from '../RemoteConfigKeys.js';

import getListData from '../selector/getListData.js';
import getFeedName from '../selector/getFeedName.js';
import getProductData from '../selector/getProductData.js';
import getOperationData from '../selector/getOperationData.js';

import fetchFeeds from '../action/fetchFeeds.js';
import fetchProductDetail from '../action/fetchProductDetail.js';

import { getFeedNameType } from '../resource/feedUtils.js';
import { ProductType } from '../resource/shopConstants.js';

const cache = new LRUCache({ max: 256 });
const getSelectPath = ({ feedName } = {}) => {
  const key = `${feedName}`;
  let result = cache.get(key);
  if (!result) {
    result = ['home', getFeedNameType({ feedName }), feedName];
    cache.set(key, result);
  }
  return result;
};

const withFetchProductData = BaseComponent => {
  class FetchProductData extends React.Component {
    constructor(props) {
      super(props);
      const { staticContext } = props;
      if (staticContext.actions) {
        staticContext.actions.push({
          function: this.fetchFeeds,
          argumentObject: {},
        });
        staticContext.actions.push({
          function: this.fetchProductDetail,
          argumentObject: {},
        });
      }
    }
    fetchFeeds = () => {
      const { feedName, staticContext, fetchFeeds } = this.props;
      if (!feedName) {
        return;
      }
      return fetchFeeds({
        type: feedName,
        httpProxyHeaders: staticContext.httpProxyHeaders,
      });
    };
    fetchProductDetail = () => {
      const { productId, staticContext, fetchProductDetail } = this.props;
      if (!productId) {
        return;
      }
      return fetchProductDetail({
        productId,
        httpProxyHeaders: staticContext.httpProxyHeaders,
      });
    };
    componentDidMount() {
      this.fetchFeeds();
      this.fetchProductDetail();
    }
    componentDidUpdate(prevProps) {
      const { feedName, productId } = this.props;
      const isFeedNameChanged = feedName !== prevProps.feedName;
      const isProductIdChanged = productId !== prevProps.productId;
      if (isFeedNameChanged) {
        this.fetchFeeds();
      }
      if (isProductIdChanged) {
        this.fetchProductDetail();
      }
    }
    render() {
      return <BaseComponent {...this.props} />;
    }
  }

  FetchProductData.propTypes = {
    shopId: PropTypes.string,
    feedName: PropTypes.string,
    productId: PropTypes.string,
    messageIds: PropTypes.array,
    totalCount: PropTypes.number,
    staticContext: PropTypes.object,
    children: PropTypes.func,
    fetchFeeds: PropTypes.func,
    fetchProductDetail: PropTypes.func,
  };
  FetchProductData.defaultProps = {
    shopId: null,
    feedName: null,
    productId: null,
    messageIds: [],
    totalCount: null,
    staticContext: {},
    children: () => null,
    fetchFeeds: () => null,
    fetchProductDetail: () => null,
  };

  const mapStateToProps = (state, { productId }) => {
    const shopId = getOperationData(state, ['shop'], 'usingShopId');
    const productType = getProductData(state, [productId], 'type');
    const feedName =
      ProductType.MESSAGEPACKS === productType
        ? getFeedName(state, MESSAGE_PACK_FEED_ALIAS, '{PRODUCT_ID}', productId)
        : undefined;
    const listSelectPath = getSelectPath({ feedName });
    return {
      shopId,
      feedName,
      productId,
      messageIds: getListData(state, listSelectPath, 'itemIds'),
      totalCount: getListData(state, listSelectPath, 'totalCount'),
    };
  };

  const mapDispatchToProps = dispatch => {
    return {
      fetchFeeds: data => dispatch(fetchFeeds(data)),
      fetchProductDetail: data => dispatch(fetchProductDetail(data)),
    };
  };

  return connect(mapStateToProps, mapDispatchToProps)(FetchProductData);
};

export default withFetchProductData;
